import React, { useRef } from "react";
import PropTypes from "prop-types";
import FormFieldErrorMessage from "@JohnLewisPartnership/checkout-forms/dist/components/form-field-error-message/FormFieldErrorMessage";
import formFieldStyles from "@JohnLewisPartnership/checkout-forms/dist/components/form-field/form-field.module.scss";
import { SecondaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import Input from "@JohnLewisPartnership/jl-design-system/dist/elements/input/Input";
import mime from "mime/lite";
import styles from "./image-upload.module.scss";
import UploadPreview from "./upload-preview/upload-preview";

function ImageUpload(props) {
  const {
    onAttachmentAdded,
    maxUploads,
    onAttachmentRemoved,
    attachments,
    id,
    describedById,
    meta,
    supportedMediaTypePrefix,
  } = props;

  const inputEl = useRef(null);

  const isNotSupportedMedia = mimeType =>
    mimeType && supportedMediaTypePrefix && mimeType.indexOf(`${supportedMediaTypePrefix}/`) === -1;

  const isValidFileSize = file => {
    const fiveMbLimit = 5 * 1024 * 1024;
    return file.size < fiveMbLimit;
  };

  const readFile = file => {
    const reader = new FileReader();
    reader.onloadend = () => {
      onAttachmentAdded(file, reader.result);
    };
    reader.readAsDataURL(file);
  };

  const loadFile = file => {
    const hh = require("heic2any");
    if (file.name.indexOf("heic") > -1) {
      hh({ blob: file, toType: "image/jpeg", quality: 0.7 }).then(readFile);
    } else {
      readFile(file);
    }
  };

  const invalidFile = () => {
    onAttachmentAdded(null, null, {
      message: `Sorry, we can't upload this type of image. Please provide 
              images in formats like jpg, jpeg, png, heic or gif.`,
    });
  };

  const handleUploadImageButtonClick = () => {
    if (inputEl && inputEl.current && inputEl.current.input && inputEl.current.input.click) {
      inputEl.current.input.click();
    }
  };

  const handleImageChange = event => {
    event.preventDefault();
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (!isValidFileSize(file)) {
      onAttachmentAdded(null, null, {
        message: `Sorry, we can't upload images larger than 5MB. Please 
        reduce size and try again.`,
      });
      return;
    }

    if (isNotSupportedMedia(mime.getType(file.name))) {
      invalidFile();
      return;
    }

    loadFile(file).catch(() => {
      invalidFile();
    });
  };

  const hasError = meta.error && meta.touched;
  return (
    <div className={styles["image-upload"]}>
      <SecondaryButton
        className={styles["image-upload__button"]}
        disabled={attachments.length >= maxUploads}
        onClick={handleUploadImageButtonClick}
      >
        Upload an image
        <Input
          {...props}
          ref={inputEl}
          onChange={handleImageChange}
          disabled={attachments.length >= maxUploads}
          className={styles["image-upload__input"]}
        />
      </SecondaryButton>
      {hasError && <FormFieldErrorMessage id={id} describedById={`${describedById}-error`} message={meta.error} />}

      <div className={formFieldStyles.formFieldInstruction}>
        Upload up to <strong>6 images, no larger than 5MB each</strong>
      </div>

      {attachments.length ? <UploadPreview attachments={attachments} onImageRemoved={onAttachmentRemoved} /> : null}
    </div>
  );
}

ImageUpload.propTypes = {
  ...Input.propTypes,
  onAttachmentAdded: PropTypes.func.isRequired,
  onAttachmentRemoved: PropTypes.func.isRequired,
  maxUploads: PropTypes.number.isRequired,
  supportedMediaTypePrefix: PropTypes.string,
};

ImageUpload.defaultProps = {
  ...Input.defaultProps,
  supportedMediaTypePrefix: undefined,
};

export default ImageUpload;
